import React, { forwardRef, useCallback, useRef, useState } from 'react';
import DataGrid from 'devextreme-react/data-grid';
import './BaseDataGrid.css';

const BaseDataGrid = forwardRef(({
  onSelectionChanged: onSelectionChangedProp,
  onOptionChanged: onOptionChangedProp,
  onContentReady: onContentReadyProp,
  children,
  height = '100%',
  ...rest
}, ref) => {
  const internalRef = useRef(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const [activeFilters, setActiveFilters] = useState([]);

  const setRefs = useCallback((node) => {
    internalRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const updateActiveFilters = useCallback(() => {
    if (!internalRef.current?.instance) return;
    const instance = internalRef.current.instance();
    const filters = [];
    const colCount = instance.columnCount();
    for (let i = 0; i < colCount; i++) {
      const filterValue = instance.columnOption(i, 'filterValue');
      const filterValues = instance.columnOption(i, 'filterValues');
      const hasFilterValue = filterValue !== undefined && filterValue !== null && filterValue !== '';
      const hasFilterValues = Array.isArray(filterValues) && filterValues.length > 0;
      if (hasFilterValue || hasFilterValues) {
        const caption = instance.columnOption(i, 'caption') || instance.columnOption(i, 'dataField');
        filters.push(caption);
      }
    }
    setActiveFilters(prev => {
      const prevKey = prev.join(',');
      const newKey = filters.join(',');
      return prevKey === newKey ? prev : filters;
    });
  }, []);

  const handleSelectionChanged = useCallback((e) => {
    setSelectedCount(e.selectedRowsData?.length || 0);
    if (onSelectionChangedProp) onSelectionChangedProp(e);
  }, [onSelectionChangedProp]);

  const handleOptionChanged = useCallback((e) => {
    if (e.fullName?.includes('filterValue') || e.fullName?.includes('filterValues') || e.name === 'filterValue') {
      setTimeout(updateActiveFilters, 0);
    }
    if (onOptionChangedProp) onOptionChangedProp(e);
  }, [updateActiveFilters, onOptionChangedProp]);

  const handleContentReady = useCallback((e) => {
    updateActiveFilters();
    if (onContentReadyProp) onContentReadyProp(e);
  }, [updateActiveFilters, onContentReadyProp]);

  const clearFilters = useCallback(() => {
    if (internalRef.current?.instance) {
      internalRef.current.instance().clearFilter();
      setActiveFilters([]);
    }
  }, []);

  const clearSelection = useCallback(() => {
    if (internalRef.current?.instance) {
      internalRef.current.instance().clearSelection();
      setSelectedCount(0);
    }
  }, []);

  const showBar = activeFilters.length > 0 || selectedCount > 0;

  return (
    <div className="webx-base-grid-wrapper" style={{ height }}>
      <DataGrid
        ref={setRefs}
        height="100%"
        onSelectionChanged={handleSelectionChanged}
        onOptionChanged={handleOptionChanged}
        onContentReady={handleContentReady}
        {...rest}
      >
        {children}
      </DataGrid>
      {showBar && (
        <div className="webx-grid-bottom-bars">
          <div className="webx-grid-status-bar">
            {activeFilters.length > 0 && (
              <div className="webx-grid-status-section">
                <span className="webx-grid-status-label">Filters:</span>
                {activeFilters.map((name, i) => (
                  <span key={i} className="webx-grid-filter-chip">{name}</span>
                ))}
                <button className="webx-grid-status-clear" onClick={clearFilters}>Clear Filters</button>
              </div>
            )}
            {activeFilters.length > 0 && selectedCount > 0 && (
              <div className="webx-grid-status-divider" />
            )}
            {selectedCount > 0 && (
              <div className="webx-grid-status-section">
                <span>{selectedCount} row{selectedCount !== 1 ? 's' : ''} selected</span>
                <button className="webx-grid-status-clear" onClick={clearSelection}>Clear</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

BaseDataGrid.displayName = 'BaseDataGrid';
export default BaseDataGrid;
